/**
 * settings/cards — the existing settings cards, extracted from the former
 * monolithic SettingsPage so the T12.2 grouped-menu surfaces can rehome each
 * one without losing behavior. Nothing about the cards' logic changed in the
 * extraction; they are the same components the single-page Settings shipped,
 * now individually exported so:
 *   - Account & members  composes HouseholdCard · SwitcherCard · MembersCard ·
 *                        InvitesCard · JoinHouseholdCard · SessionCard ·
 *                        DangerZoneCard
 *   - App settings       composes LocationCard · StoresCard ·
 *                        and the (rehomed) notification / appearance rows
 *
 * Shared styles + the session-mutation / date helpers live here too so both the
 * surfaces and any regression coverage import from one place.
 *
 * Token handling: join/switch rotate the access token — those cards call
 * `tokens.setSession` with the new `sessionToken`, then clear the query cache so
 * the next reads refetch with the new household scope. Leave-current returns
 * `loggedOut: true`; the SessionCard falls through to the shared `logout`.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { useAutoSaveField } from './useAutoSaveField';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  keys,
  useAddStoresToSelection,
  useCancelHouseholdInvite,
  useCreateHouseholdInvite,
  useCurrentHousehold,
  useDeleteCurrentHousehold,
  useDeleteStore,
  useDiscoverNearbyStores,
  useHouseholdInvites,
  useHouseholds,
  useJoinHousehold,
  useLeaveHousehold,
  useRemoveHouseholdUser,
  useSelectedStores,
  useSetSelectedStores,
  useStores,
  useSwitchHousehold,
  useToggleStoreFavorite,
  useUpdateCurrentHousehold,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import { repairTypoTerm, type RewriteCatalog } from '@shopista/shared/search';
import type {
  DiscoveredStore,
  Household,
  HouseholdMembershipMutationResponse,
  HouseholdRole,
  HouseholdUser,
  OnlineStoreCandidate,
  SelectedStoresResponse,
  StoreLocation,
} from '@shopista/shared/types';
import { Button, Card, Icon, Input, List, ListItem, Modal, Spinner } from '../../components';
import {
  formatNextOpen,
  formatTodayHours,
  isOpenNow,
} from '../../utils/isOpenNow';
import { reverseGeocode } from '../../utils/reverseGeocode';
import { acquireDeviceLocation } from '../../stores/deviceLocation';
import { useTokens } from '../../auth';
import { routes } from '../../routes/paths';

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

// T12.2 step 11e — the rehomed cards read in the same `.setgroup` vocabulary as
// the new preference surfaces: a white card with a hairline border (no shadow),
// compact padding, and section headings at the prototype's `h4` size
// (`--font-size-sm`, not the former `--font-size-md`). Presentational only — no
// logic/handler change. `setgroupCardStyle` is spread into each Card's `style`
// (and the Card rendered with `shadow="none"`); `setgroupCardPaddedStyle` adds
// the `--space-4` padding for cards that don't manage their own inner padding.
const HAIRLINE = '0.0625rem';

export const sectionHeaderStyle: CSSProperties = {
  // T47 Phase 4 — settings card section headings (<h3>) → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  margin: 0,
};

const setgroupCardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
};

export const setgroupCardPaddedStyle: CSSProperties = {
  ...setgroupCardStyle,
  padding: 'var(--space-4)',
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const fieldValueStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
  fontWeight: 'var(--font-weight-medium)',
};

const emptyStyle: CSSProperties = {
  padding: 'var(--space-4)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
};

// T45 — the discovery-radius client gate mirrors the backend cap
// (`updateHouseholdSchema.discoveryRadiusKm` = min(1).max(50) in
// backend/src/household/household.ts). Keep these in sync with that schema; the
// backend stays the source of truth, the client just clamps to avoid a 400.
const RADIUS_MIN_KM = 1;
const RADIUS_MAX_KM = 50;

// T54 — past this many discovered candidates the checklist collapses to a
// compact head (the nearest CHECKLIST_COLLAPSE_LIMIT rows plus every CHECKED
// row) so the screen's primary actions — the wizard's Done on first run, the
// dismiss row in Settings — stay within a short scroll. A "Show all N stores"
// toggle expands the rest; the filter always searches the FULL list.
const CHECKLIST_COLLAPSE_LIMIT = 8;

const hintStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

// "Saved" auto-save feedback — a muted success affordance shown briefly after a
// field commits (T30.5). Quiet by design: auto-save should reassure, not shout.
const savedStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-status-success)',
  fontWeight: 'var(--font-weight-medium)',
};

const fieldRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  marginBottom: 'var(--space-3)',
};

const inlineRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

// MN7 — the store-row trailing cluster compresses to fit 390px: the favourite
// ★ stays the one always-visible primary control, and ✎ Edit + × Remove move
// behind a single ⋯ "more" button that opens this small popover menu. The
// cluster anchors the menu via `position: relative` so it overlays the row.
const moreAnchorStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
};

const moreMenuStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + var(--space-1))',
  right: 0,
  zIndex: 'var(--z-sticky)' as unknown as number,
  minWidth: '9rem',
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--space-1)',
  backgroundColor: 'var(--color-bg-raised)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-md)',
};

const moreMenuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-3)',
  border: 0,
  background: 'transparent',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  textAlign: 'left',
};

// ---------------------------------------------------------------------------
// Member / "You" rows — the prototype `.prow` card (T12.2 step 11f). A bordered
// row with an emoji tile, a name + role pill, the cadence line, and a caret.
// Presentational only — the tap-to-detail + manager-remove logic is unchanged.
// ---------------------------------------------------------------------------

const prowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

const prowThumbStyle: CSSProperties = {
  width: '2.625rem',
  height: '2.625rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const prowMetaStyle: CSSProperties = { flex: 1, minWidth: 0 };

const prowNameStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

const prowCadStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

const prowCaretStyle: CSSProperties = {
  color: 'var(--color-border-strong)',
  fontSize: 'var(--font-size-sm)',
  flex: '0 0 auto',
};

const rolePillBaseStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  padding: '0.125rem var(--space-2)',
  borderRadius: 'var(--radius-pill)',
  flex: '0 0 auto',
};

const rolePillMutedStyle: CSSProperties = {
  ...rolePillBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-muted)',
};

// `.setrow` — a label + trailing control row inside a setgroup (prototype).
// Used by the "You" group's profile + leave rows (T12.2 step 11f).
const setrowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) 0',
  borderBottomWidth: HAIRLINE,
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
};

const setrowLabelStyle: CSSProperties = { flex: 1, minWidth: 0 };

const setrowTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const setrowDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

// Owner pill — tinted (prototype `.rolepill.owner`: brand-light fill, white text).
const rolePillOwnerStyle: CSSProperties = {
  ...rolePillBaseStyle,
  backgroundColor: 'var(--color-brand-purple-light)',
  color: 'var(--color-text-inverse)',
};

// ---------------------------------------------------------------------------
// Household card (name + invite code + rename).
// ---------------------------------------------------------------------------

interface HouseholdCardProps {
  householdLoading: boolean;
  householdError: unknown;
  household:
    | {
        name: string;
        invite_code: string;
      }
    | null
    | undefined;
  isManager: boolean;
}

export function HouseholdCard({
  householdLoading,
  householdError,
  household,
  isManager,
}: HouseholdCardProps): JSX.Element {
  const update = useUpdateCurrentHousehold();
  const [localError, setLocalError] = useState<string | null>(null);

  // T30.5 — the household name auto-saves: a manager edits the field inline and
  // it commits on blur or Enter (no Rename/Save/Cancel edit-session). An empty
  // name never writes (validity gate); an unchanged name is a no-op.
  const name = useAutoSaveField({
    initial: household?.name ?? '',
    validate: (v) => v.trim().length > 0,
    onCommit: async (next) => {
      setLocalError(null);
      try {
        await update.mutateAsync({ name: next });
      } catch (err) {
        setLocalError(apiErrorMessage(err, 'Rename failed.'));
      }
    },
  });

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-household"
    >
      <h3 style={sectionHeaderStyle}>Household</h3>
      <p style={hintStyle}>
        Everyone you invite shares this list, preferences, and trips — changes
        sync to all members.
      </p>
      {householdLoading ? (
        <div style={emptyStyle}>
          <Spinner />
        </div>
      ) : householdError ? (
        <div style={errorStyle} role="alert">
          {householdError instanceof Error
            ? householdError.message
            : String(householdError)}
        </div>
      ) : household ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <div style={fieldRowStyle}>
            <span style={fieldLabelStyle}>Name</span>
            {isManager ? (
              <div style={inlineRowStyle}>
                <Input
                  label=""
                  aria-label="Household name"
                  value={name.value}
                  onChange={(e) => name.setValue(e.target.value)}
                  onBlur={name.onBlur}
                  onKeyDown={name.onKeyDown}
                  data-testid="settings-household-name-input"
                />
                {name.saved && (
                  <span style={savedStyle} data-testid="settings-household-name-saved">
                    Saved
                  </span>
                )}
              </div>
            ) : (
              <span style={fieldValueStyle} data-testid="settings-household-name">
                {household.name}
              </span>
            )}
            {localError && (
              <span style={errorStyle} role="alert">
                {localError}
              </span>
            )}
          </div>
          <div style={fieldRowStyle}>
            <span style={fieldLabelStyle}>Invite code</span>
            <span style={fieldValueStyle} data-testid="settings-household-invite">
              {household.invite_code}
            </span>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Household switcher card (only shown when user belongs to > 1 household)
// ---------------------------------------------------------------------------

export function SwitcherCard(): JSX.Element {
  const householdsQuery = useHouseholds();
  const households = householdsQuery.data ?? [];
  const tokens = useTokens();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const switchHh = useSwitchHousehold();
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleSwitch = async (householdId: string) => {
    setError(null);
    setPendingId(householdId);
    try {
      const resp = await switchHh.mutateAsync({ householdId });
      applySessionMutation(resp, tokens);
      queryClient.clear();
      navigate(routes.home, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Switch failed.'));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardStyle}
      data-testid="settings-switcher"
    >
      <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
        <h3 style={sectionHeaderStyle}>Your households</h3>
        <p style={hintStyle}>Switch which household is active.</p>
      </div>
      {error && (
        <div style={{ ...emptyStyle, ...errorStyle }} role="alert">
          {error}
        </div>
      )}
      <List data-testid="settings-switcher-list">
        {households.map((h, index) => (
          <ListItem
            key={h.id}
            divided={index > 0}
            data-testid={`settings-switcher-${h.id}`}
            trailing={
              h.is_current ? (
                <span style={hintStyle}>Active</span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={pendingId === h.id}
                  disabled={pendingId !== null}
                  onClick={() => handleSwitch(h.id)}
                  data-testid={`settings-switcher-${h.id}-switch`}
                >
                  Switch
                </Button>
              )
            }
          >
            <div>
              <div style={fieldValueStyle}>{h.name}</div>
              <div style={hintStyle}>
                {h.role} · joined {formatDate(h.joined_at)}
              </div>
            </div>
          </ListItem>
        ))}
      </List>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Members card — gains the age/exclusions cadence line + a tappable
// row that routes to the member detail screen (T12.2).
// ---------------------------------------------------------------------------

interface MembersCardProps {
  users: HouseholdUser[];
  loading: boolean;
  error: unknown;
  currentUserId: string | undefined;
  isManager: boolean;
  /** Open the member-detail surface for a member (T12.2). */
  onOpenMember?: (userId: string) => void;
}

/** Build the cadence summary line shown under a member's name (T12.2). */
export function memberCadenceLine(user: HouseholdUser, isSelf: boolean): string {
  const parts: string[] = [];
  if (user.age_group) {
    parts.push(user.age_group.charAt(0).toUpperCase() + user.age_group.slice(1));
  }
  const exclusions = user.exclusions ?? [];
  for (const ex of exclusions) parts.push(ex);
  if (isSelf) parts.push('you');
  return parts.length > 0 ? parts.join(' · ') : 'No details set';
}

export function MembersCard({
  users,
  loading,
  error,
  currentUserId,
  isManager,
  onOpenMember,
}: MembersCardProps): JSX.Element {
  const removeUser = useRemoveHouseholdUser();
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleRemove = async (userId: string, label: string) => {
    setActionError(null);
    if (
      !window.confirm(
        `Remove ${label} from the household? They will lose access to shared lists and products.`
      )
    ) {
      return;
    }
    setPendingId(userId);
    try {
      await removeUser.mutateAsync(userId);
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Remove failed.'));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-members"
    >
      <h3 style={sectionHeaderStyle}>Members</h3>
      <p style={hintStyle}>
        Tap a member to set their age and any dietary exclusions.
      </p>
      {actionError && (
        <div style={{ ...errorStyle, marginBottom: 'var(--space-2)' }} role="alert">
          {actionError}
        </div>
      )}
      {loading ? (
        <div style={emptyStyle}>
          <Spinner />
        </div>
      ) : error ? (
        <div style={{ ...emptyStyle, ...errorStyle }} role="alert">
          {apiErrorMessage(error, String(error))}
        </div>
      ) : users.length === 0 ? (
        <div style={emptyStyle}>No members found.</div>
      ) : (
        <div
          data-testid="settings-members-list"
          style={{ marginTop: 'var(--space-3)' }}
        >
          {users.map((user) => {
            const isSelf = currentUserId === user.id;
            const label = user.display_name ?? user.email;
            // The membership role is manager | member; the manager wears the
            // tinted pill (the prototype's "Owner" tint).
            const isOwner = user.role === 'manager';
            const openDetail = onOpenMember
              ? () => onOpenMember(user.id)
              : undefined;
            return (
              <div
                key={user.id}
                role={openDetail ? 'button' : undefined}
                tabIndex={openDetail ? 0 : undefined}
                style={{ ...prowStyle, cursor: openDetail ? 'pointer' : 'default' }}
                data-testid={`settings-member-${user.id}`}
                onClick={openDetail}
                onKeyDown={
                  openDetail
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail();
                        }
                      }
                    : undefined
                }
              >
                <span style={prowThumbStyle} aria-hidden="true">
                  <Icon name="person" />
                </span>
                <div style={prowMetaStyle}>
                  <div style={prowNameStyle}>
                    <span>
                      {label}
                      {isSelf ? ' (you)' : ''}
                    </span>
                    <span style={isOwner ? rolePillOwnerStyle : rolePillMutedStyle}>
                      {user.role}
                    </span>
                  </div>
                  <div
                    style={prowCadStyle}
                    data-testid={`settings-member-${user.id}-cadence`}
                  >
                    {memberCadenceLine(user, isSelf)}
                  </div>
                </div>
                {isManager && !isSelf && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={pendingId === user.id}
                    disabled={pendingId !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRemove(user.id, label);
                    }}
                    data-testid={`settings-member-${user.id}-remove`}
                  >
                    Remove
                  </Button>
                )}
                {openDetail && (
                  <span style={prowCaretStyle} aria-hidden="true">
                    <Icon name="chevron-right" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Invites card (manager only)
// ---------------------------------------------------------------------------

export function InvitesCard(): JSX.Element {
  const invitesQuery = useHouseholdInvites();
  const createInvite = useCreateHouseholdInvite();
  const cancelInvite = useCancelHouseholdInvite();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<HouseholdRole>('member');
  const [formError, setFormError] = useState<string | null>(null);
  const [justCreatedUrl, setJustCreatedUrl] = useState<string | null>(null);
  const [cancelPendingId, setCancelPendingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setJustCreatedUrl(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setFormError('Email is required.');
      return;
    }
    try {
      const resp = await createInvite.mutateAsync({ email: trimmed, role });
      setJustCreatedUrl(resp.inviteUrl);
      setEmail('');
      setRole('member');
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Invite failed.'));
    }
  };

  const handleCancel = async (inviteId: string, inviteEmail: string) => {
    setCancelError(null);
    if (
      !window.confirm(
        `Cancel invite to ${inviteEmail}? They will need a new invite to join.`
      )
    ) {
      return;
    }
    setCancelPendingId(inviteId);
    try {
      await cancelInvite.mutateAsync(inviteId);
    } catch (err) {
      setCancelError(apiErrorMessage(err, 'Cancel failed.'));
    } finally {
      setCancelPendingId(null);
    }
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard may be blocked; the URL is still displayed for manual copy.
    }
  };

  const invites = invitesQuery.data?.invites ?? [];
  const pending = invites.filter((i) => !i.accepted_at);

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-invites"
    >
      <h3 style={sectionHeaderStyle}>Invite members</h3>
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: 'var(--space-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
        data-testid="settings-invites-form"
      >
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="settings-invites-email"
          placeholder="person@example.com"
        />
        <div style={fieldRowStyle}>
          <span style={fieldLabelStyle}>Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as HouseholdRole)}
            data-testid="settings-invites-role"
            style={{
              fontFamily: 'var(--font-family-sans)',
              fontSize: 'var(--font-size-base)',
              padding: 'var(--space-2) var(--space-3)',
              borderWidth: '0.0625rem',
              borderStyle: 'solid',
              borderColor: 'var(--color-border-strong)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
            }}
          >
            <option value="member">Member</option>
            <option value="manager">Manager</option>
          </select>
        </div>
        {formError && (
          <span style={errorStyle} role="alert">
            {formError}
          </span>
        )}
        <div>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={createInvite.isPending}
            disabled={createInvite.isPending}
            data-testid="settings-invites-submit"
          >
            Send invite
          </Button>
        </div>
      </form>

      {justCreatedUrl && (
        <div
          style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            borderWidth: '0.0625rem',
            borderStyle: 'solid',
            borderColor: 'var(--color-border-strong)',
            borderRadius: 'var(--radius-md)',
          }}
          data-testid="settings-invites-created"
        >
          <div style={{ ...fieldLabelStyle, marginBottom: 'var(--space-1)' }}>
            Invite link
          </div>
          <div
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              wordBreak: 'break-all',
              marginBottom: 'var(--space-2)',
            }}
          >
            {justCreatedUrl}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyUrl(justCreatedUrl)}
            data-testid="settings-invites-copy"
          >
            Copy
          </Button>
        </div>
      )}

      <h4
        style={{
          ...sectionHeaderStyle,
          fontSize: 'var(--font-size-sm)',
          marginTop: 'var(--space-4)',
          marginBottom: 'var(--space-2)',
        }}
      >
        Pending invites
      </h4>
      {cancelError && (
        <div style={{ ...errorStyle, marginTop: 'var(--space-2)' }} role="alert">
          {cancelError}
        </div>
      )}
      {invitesQuery.isLoading ? (
        <Spinner />
      ) : pending.length === 0 ? (
        <div style={hintStyle}>No pending invites.</div>
      ) : (
        <List data-testid="settings-invites-list">
          {pending.map((invite, index) => (
            <ListItem
              key={invite.id}
              divided={index > 0}
              data-testid={`settings-invite-${invite.id}`}
              trailing={
                <div style={inlineRowStyle}>
                  <span style={hintStyle}>{invite.role}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={cancelPendingId === invite.id}
                    disabled={cancelPendingId !== null}
                    onClick={() => handleCancel(invite.id, invite.email)}
                    data-testid={`settings-invite-${invite.id}-cancel`}
                  >
                    Cancel
                  </Button>
                </div>
              }
            >
              <div>
                <div style={fieldValueStyle}>{invite.email}</div>
                <div style={hintStyle}>
                  Expires {formatDate(invite.expires_at)}
                </div>
              </div>
            </ListItem>
          ))}
        </List>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Join another household
// ---------------------------------------------------------------------------

export function JoinHouseholdCard(): JSX.Element {
  const tokens = useTokens();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const join = useJoinHousehold();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter an invite code or token.');
      return;
    }
    try {
      const resp =
        trimmed.length > 12
          ? await join.mutateAsync({ inviteToken: trimmed })
          : await join.mutateAsync({ inviteCode: trimmed });
      applySessionMutation(resp, tokens);
      queryClient.clear();
      navigate(routes.home, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Join failed.'));
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-join"
    >
      <h3 style={sectionHeaderStyle}>Join another household</h3>
      <p style={hintStyle}>
        Enter an invite code or paste an invite link from a manager of that
        household.
      </p>
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: 'var(--space-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
        data-testid="settings-join-form"
      >
        <Input
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-testid="settings-join-code"
        />
        {error && (
          <span style={errorStyle} role="alert">
            {error}
          </span>
        )}
        <div>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={join.isPending}
            disabled={join.isPending}
            data-testid="settings-join-submit"
          >
            Join
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Session + leave current household
// ---------------------------------------------------------------------------

interface SessionCardProps {
  email: string | undefined;
  onLogout: () => Promise<void> | void;
  canLeaveCurrent: boolean;
  householdName: string | undefined;
}

export function SessionCard({
  email,
  onLogout,
  canLeaveCurrent,
  householdName,
}: SessionCardProps): JSX.Element {
  const tokens = useTokens();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const leave = useLeaveHousehold();
  const householdQuery = useCurrentHousehold();
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const handleLeave = async () => {
    setLeaveError(null);
    const householdId = householdQuery.data?.id;
    if (!householdId) {
      setLeaveError('No household to leave.');
      return;
    }
    const ok = window.confirm(
      `Leave "${householdName ?? 'this household'}"? You will lose access to its shared lists and products.`
    );
    if (!ok) return;
    try {
      const resp = await leave.mutateAsync(householdId);
      if (resp.loggedOut) {
        await onLogout();
        return;
      }
      applySessionMutation(resp, tokens);
      queryClient.clear();
      navigate(routes.home, { replace: true });
    } catch (err) {
      setLeaveError(apiErrorMessage(err, 'Leave failed.'));
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-session"
    >
      <h3 style={sectionHeaderStyle}>You</h3>
      <div style={{ marginTop: 'var(--space-2)' }}>
        {/* Your profile — name/email summary (prototype `.setrow`). */}
        <div style={setrowStyle}>
          <div style={setrowLabelStyle}>
            <div style={setrowTitleStyle}>Your profile</div>
            {email && (
              <div style={setrowDescStyle} data-testid="settings-email">
                {email}
              </div>
            )}
          </div>
        </div>
        {/* Leave household — danger-styled row (prototype `.setrow` + red). */}
        {canLeaveCurrent && (
          <button
            type="button"
            style={{
              ...setrowStyle,
              borderBottom: 0,
              width: '100%',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onClick={handleLeave}
            disabled={leave.isPending}
            data-testid="settings-leave"
          >
            <div style={setrowLabelStyle}>
              <div
                style={{ ...setrowTitleStyle, color: 'var(--color-status-danger)' }}
              >
                Leave household
              </div>
              <div style={setrowDescStyle}>
                Remove yourself from {householdName ?? 'this household'}
              </div>
            </div>
            <span style={prowCaretStyle} aria-hidden="true">
              <Icon name="chevron-right" />
            </span>
          </button>
        )}
        {leaveError && (
          <div style={{ ...errorStyle, marginTop: 'var(--space-2)' }} role="alert">
            {leaveError}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Danger zone — manager-only "Delete household" with type-to-confirm modal.
// ---------------------------------------------------------------------------

interface DangerZoneCardProps {
  householdId: string;
  householdName: string;
  onLogout: () => Promise<void> | void;
}

export function DangerZoneCard({
  householdId,
  householdName,
  onLogout,
}: DangerZoneCardProps): JSX.Element {
  const navigate = useNavigate();
  const deleteHousehold = useDeleteCurrentHousehold();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openModal = () => {
    setConfirmText('');
    setError(null);
    setOpen(true);
  };
  const closeModal = () => {
    if (deleteHousehold.isPending) return;
    setOpen(false);
    setError(null);
    setConfirmText('');
  };

  const canDelete =
    confirmText.trim() === householdName && !deleteHousehold.isPending;

  const handleDelete = async () => {
    if (!canDelete) return;
    setError(null);
    try {
      await deleteHousehold.mutateAsync({ householdId, deleteMembers: true });
      await onLogout();
      navigate(routes.login, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Delete failed.'));
    }
  };

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-danger-zone"
    >
      <h3 style={sectionHeaderStyle}>Danger zone</h3>
      <p style={hintStyle}>
        Permanently delete this household and everything in it. This cannot be
        undone.
      </p>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <Button
          variant="danger"
          onClick={openModal}
          data-testid="settings-delete-household"
        >
          Delete household
        </Button>
      </div>
      <Modal
        open={open}
        onClose={closeModal}
        title="Delete household"
        ariaLabel="Confirm delete household"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
          }}
        >
          <p style={{ margin: 0 }} data-testid="settings-delete-household-warning">
            This permanently deletes <strong>{householdName}</strong> and all its
            data — lists, products, offers, members. This cannot be undone.
          </p>
          <div style={fieldRowStyle}>
            <span style={fieldLabelStyle}>
              Type the household name to confirm
            </span>
            <Input
              label=""
              aria-label="Household name confirmation"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="settings-delete-household-confirm-input"
              autoFocus
            />
          </div>
          {error && (
            <div style={errorStyle} role="alert">
              {error}
            </div>
          )}
          <div style={{ ...inlineRowStyle, justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeModal}
              disabled={deleteHousehold.isPending}
              data-testid="settings-delete-household-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              loading={deleteHousehold.isPending}
              disabled={!canDelete}
              data-testid="settings-delete-household-confirm"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Location card (household coords + auto-discover nearby stores)
// ---------------------------------------------------------------------------

interface LocationCardProps {
  household: Household | null | undefined;
  isManager: boolean;
  onDiscoveredStores: (candidates: DiscoveredStore[] | null) => void;
  /**
   * BUG57 — the PATCH response carries `onlineStores` on every save (not
   * gated on coord resolution the way `discoveredStores` is), so a location
   * save can surface online-store candidates too. Optional: existing callers
   * (e.g. AppSettingsPage's stand-alone LocationCard, which has no online-store
   * wiring) don't need to pass it; FirstRunSetup wires it to seed StoresCard's
   * online group without an extra "Find nearby stores" click.
   */
  onOnlineStores?: (candidates: OnlineStoreCandidate[]) => void;
}

function describeGeocodeError(code: string): string {
  switch (code) {
    case 'ZERO_RESULTS':
      return "We couldn't find that address on the map. Use 'Use my current location' to set coordinates from GPS.";
    case 'NETWORK_ERROR':
      return "Couldn't reach the geocoding service to look up your address. The address was saved — try 'Use my current location' for coordinates.";
    case 'GEOCODING_UNAVAILABLE':
      return 'Address geocoding is disabled on this server. Use "Use my current location" to set coordinates from GPS.';
    case 'OUT_OF_AREA':
      return "That address resolved outside the postal code's province. Check the address or postal code, or use 'Use my location'.";
    case 'EMPTY_ADDRESS':
    case 'INVALID_RESPONSE':
      return "We couldn't read coordinates from your address. Try refining the address or use 'Use my current location'.";
    default:
      return `Address saved, but coordinates lookup failed (${code}). Use 'Use my current location' to set coordinates.`;
  }
}

export function LocationCard({
  household,
  isManager,
  onDiscoveredStores,
  onOnlineStores,
}: LocationCardProps): JSX.Element {
  const update = useUpdateCurrentHousehold();
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const seededAddress =
    household?.address ??
    [household?.city, household?.state_province].filter(Boolean).join(', ');
  const seededPostal = household?.postal_code ?? '';

  const hasCoords = household?.latitude != null && household?.longitude != null;

  // T45 — the discovery radius moved off the location card to Store preferences,
  // where discovery actually runs. The coordinate fields (postal / address / "Use
  // my current location") keep their own auto-save and no longer carry a radius.
  const saveLocation = async (args: {
    latitude?: number;
    longitude?: number;
    address?: string;
    postalCode?: string;
    committedField?: 'address' | 'postalCode' | 'both';
  }) => {
    setError(null);
    setWarning(null);
    setMessage(null);
    try {
      const resp = await update.mutateAsync(args);
      if (resp.discoveredStores && resp.discoveredStores.length > 0) {
        onDiscoveredStores(resp.discoveredStores);
      }
      // BUG57 — carried on every save response, independent of the physical
      // `discoveredStores` gate above.
      if (resp.onlineStores && resp.onlineStores.length > 0) {
        onOnlineStores?.(resp.onlineStores);
      }
      setMessage('Location saved.');
      if (resp.geocodeError) {
        setWarning(describeGeocodeError(resp.geocodeError));
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed.'));
    }
  };

  // T30.5 — the location fields auto-save on blur/Enter. Address and postal code
  // are separate, correctly-labelled fields; geocoding prefers the full address
  // when present, else the postal code. The geocode + auto-discovery +
  // location-change re-link side-effects all live in `saveLocation`, unchanged —
  // they fire at the new commit point exactly as the old Save button fired them.

  // Address commit prefers the full address; an empty address still allows a
  // postal-only save via the postal field's own commit.
  const address = useAutoSaveField({
    initial: seededAddress,
    validate: (v) => v.trim().length > 0,
    onCommit: async (next) => {
      const payload: Parameters<typeof saveLocation>[0] = {
        address: next,
      };
      const pc = postal.value.trim();
      payload.committedField = pc ? 'both' : 'address';
      if (pc) payload.postalCode = pc;
      await saveLocation(payload);
    },
  });

  // Postal commit gates on a plausible Canadian postal / FSA shape so a
  // half-typed code never writes. Geocode prefers a full address when one is
  // present; otherwise the postal code drives the lookup.
  const postal = useAutoSaveField({
    initial: seededPostal,
    validate: (v) => /^[A-Za-z]\d[A-Za-z](\s*\d[A-Za-z]\d)?$/.test(v.trim()),
    onCommit: async (next) => {
      const payload: Parameters<typeof saveLocation>[0] = {
        postalCode: next,
      };
      const addr = address.value.trim();
      payload.committedField = addr ? 'both' : 'postalCode';
      if (addr) payload.address = addr;
      await saveLocation(payload);
    },
  });

  const [keepTrying, setKeepTrying] = useState(false);

  const handleUseMyLocation = () => {
    setError(null);
    setMessage(null);
    setKeepTrying(false);
    setCapturing(true);
    void acquireDeviceLocation({
      purpose: 'settings',
      maxAgeMs: 2 * 60 * 1000,
      accuracyTargetM: 500,
      deadlineMs: 15_000,
    }).then((outcome) => {
      const fix = outcome.kind === 'ok' ? outcome.fix : outcome.kind === 'timeout' ? outcome.partial : undefined;
      if (!fix) {
        setCapturing(false);
        setKeepTrying(true);
        setMessage(
          outcome.kind === 'denied'
            ? 'Location permission is off. You can keep trying or enter your postal code instead.'
            : 'We could not get a location yet. Keep trying or enter your postal code instead.',
        );
        return;
      }

      // Coordinates are the only required input. Dispatch their save before
      // optional network enrichment, then restore the actionable UI straight
      // away rather than making Nominatim/discovery part of this wait.
      void saveLocation({ latitude: fix.latitude, longitude: fix.longitude });
      setCapturing(false);
      void reverseGeocode(fix.latitude, fix.longitude).then((geocoded) => {
        if (!geocoded) return;
        address.setValue(geocoded.displayName);
        const payload: Parameters<typeof saveLocation>[0] = { address: geocoded.displayName };
        if (geocoded.postalCode) {
          postal.setValue(geocoded.postalCode);
          payload.postalCode = geocoded.postalCode;
        }
        void saveLocation(payload);
      });
    });
  };

  if (!household) {
    return <div />;
  }

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardPaddedStyle}
      data-testid="settings-location"
    >
      <h3 style={sectionHeaderStyle}>Home location</h3>
      <p style={hintStyle}>
        Your household location drives which stores the app suggests nearby —
        pick them on Store preferences.
        {household.store_discovery_available
          ? ''
          : ' Store discovery is disabled on this server (GOOGLE_PLACES_API_KEY not set).'}
      </p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <div style={fieldRowStyle}>
          <span style={fieldLabelStyle}>Coordinates</span>
          <span style={fieldValueStyle} data-testid="settings-location-coords">
            {hasCoords
              ? `${Number(household.latitude).toFixed(4)}, ${Number(household.longitude).toFixed(4)}`
              : 'Not set'}
          </span>
        </div>

        {isManager && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-3)',
            }}
            data-testid="settings-location-form"
          >
            <div style={inlineRowStyle}><Button type="button" variant="primary" size="sm" disabled={capturing} onClick={handleUseMyLocation} data-testid="settings-location-geolocate">{capturing ? 'Getting your location…' : keepTrying ? 'Keep trying' : 'Use my location'}</Button>{capturing && <Spinner size="sm" label="Getting your location" data-testid="settings-location-spinner" />}</div>
            <Input
              label="Postal code"
              value={postal.value}
              onChange={(e) => postal.setValue(e.target.value)}
              onBlur={postal.onBlur}
              onKeyDown={postal.onKeyDown}
              data-testid="settings-location-postal"
              placeholder="K1S 1A1"
              hint="We'll look up coordinates from your postal code. Changes save automatically."
            />
            <Input
              label="Address"
              value={address.value}
              onChange={(e) => address.setValue(e.target.value)}
              onBlur={address.onBlur}
              onKeyDown={address.onKeyDown}
              data-testid="settings-location-address"
              placeholder="123 Main St, City"
              hint="Optional — a full address geocodes more precisely than a postal code. Changes save automatically."
            />

          </div>
        )}
        {message && (
          <div
            style={{ ...hintStyle, marginBottom: 'var(--space-2)' }}
            data-testid="settings-location-message"
          >
            {message}
          </div>
        )}
        {warning && (
          <div
            style={{
              ...hintStyle,
              color: 'var(--color-status-warning, var(--color-text-muted))',
              marginBottom: 'var(--space-2)',
            }}
            role="status"
            data-testid="settings-location-warning"
          >
            {warning}
          </div>
        )}
        {error && (
          <div style={errorStyle} role="alert">
            {error}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stores card (household's imported store list + select / favorite / remove)
// ---------------------------------------------------------------------------

const statusBadgeBaseStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  padding: '0.125rem var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  display: 'inline-block',
  whiteSpace: 'nowrap',
};

const STATUS_BADGE_VARIANT: Record<
  'open' | 'closing-soon' | 'closed' | 'online',
  CSSProperties
> = {
  open: {
    backgroundColor: 'var(--color-status-success-soft, rgba(46,160,67,0.15))',
    color: 'var(--color-status-success)',
  },
  'closing-soon': {
    backgroundColor: 'var(--color-status-warning-soft, rgba(208,135,0,0.15))',
    color: 'var(--color-status-warning)',
  },
  closed: {
    backgroundColor: 'var(--color-bg-muted, rgba(0,0,0,0.06))',
    color: 'var(--color-text-secondary)',
  },
  online: {
    backgroundColor: 'var(--color-accent-soft, rgba(43,108,176,0.15))',
    color: 'var(--color-accent-primary)',
  },
};

interface StoreRowProps {
  store: StoreLocation;
  divided: boolean;
  checked: boolean;
  isManager: boolean;
  // BUG21 — `pendingSelect` / `pendingFavorite` are gone. The optimistic write
  // in `useSetSelectedStores` / `useToggleStoreFavorite` (+ rollback) replaces
  // the disable / spinner that masked the flip. `pendingDelete` stays — delete
  // is a confirm-then-spinner action, not an in-place toggle.
  pendingDelete: boolean;
  instant: Date;
  onToggleSelected: (storeId: string) => void;
  onToggleFavorite: (storeId: string) => void;
  onDelete: (store: StoreLocation) => void;
}

function StoreRow({
  store,
  divided,
  checked,
  isManager,
  pendingDelete,
  instant,
  onToggleSelected,
  onToggleFavorite,
  onDelete,
}: StoreRowProps): JSX.Element {
  // MN7 — the ⋯ overflow menu (Remove). Closes on outside-click + Escape. TD39 —
  // the Edit action is gone: store identity (name / address / hours / online) is
  // platform-authored, so a household can no longer edit it; the favourite ★ (its
  // own control) is the only per-household field left.
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const status = isOpenNow(store.opening_hours, instant, {
    isOnline: store.is_online,
  });

  let badgeLabel: string;
  let badgeVariant: keyof typeof STATUS_BADGE_VARIANT;
  // Stores have no opening_hours data yet, so the "hours completely unknown"
  // case would show an unhelpful pill on every row. Suppress the badge entirely
  // in exactly that case (no hours, not online, no nextOpenAt); every real
  // status still renders.
  let showStatusBadge = true;
  switch (status.status) {
    case 'open':
      badgeLabel = 'Open';
      badgeVariant = 'open';
      break;
    case 'closing-soon':
      badgeLabel = `Closes in ${status.minutesUntil} min`;
      badgeVariant = 'closing-soon';
      break;
    case 'online':
      badgeLabel = 'Online';
      badgeVariant = 'online';
      break;
    case 'closed':
    default:
      if (status.status === 'closed' && status.nextOpenAt) {
        badgeLabel = `Closed · Opens ${formatNextOpen(status.nextOpenAt)}`;
      } else if (!store.opening_hours) {
        badgeLabel = '';
        showStatusBadge = false;
      } else {
        badgeLabel = 'Closed';
      }
      badgeVariant = 'closed';
      break;
  }

  const todayHours =
    !store.is_online && store.opening_hours
      ? formatTodayHours(store.opening_hours, instant)
      : null;

  return (
    <ListItem
      divided={divided}
      data-testid={`settings-store-${store.id}`}
      leading={
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleSelected(store.id)}
          aria-label={`Select ${store.name}`}
          data-testid={`settings-store-${store.id}-select`}
        />
      }
      trailing={
        <div style={inlineRowStyle}>
          {/* MN5 — the favourite is the shared <Icon> star (filled = on); MN7 —
              it stays the one always-visible primary control. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleFavorite(store.id)}
            aria-label={`Toggle favorite ${store.name}`}
            data-testid={`settings-store-${store.id}-favorite`}
          >
            <Icon name={store.is_favorite ? 'star-filled' : 'star'} />
          </Button>
          {/* MN7 — Remove sits behind one ⋯ "more" button so the row cluster fits
              390px. System stores have no manage actions, so no ⋯. TD39 — Edit is
              gone (store identity is platform-authored); only Remove (unselect)
              remains. */}
          {isManager && !store.is_system && (
            <div style={moreAnchorStyle} ref={moreRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                aria-label={`More actions for ${store.name}`}
                data-testid={`settings-store-${store.id}-more`}
              >
                <Icon name="more" />
              </Button>
              {moreOpen && (
                <div
                  role="menu"
                  style={moreMenuStyle}
                  data-testid={`settings-store-${store.id}-more-menu`}
                >
                  <button
                    type="button"
                    role="menuitem"
                    style={moreMenuItemStyle}
                    onClick={() => {
                      setMoreOpen(false);
                      onDelete(store);
                    }}
                    disabled={pendingDelete}
                    aria-label={`Remove ${store.name}`}
                    data-testid={`settings-store-${store.id}-remove`}
                  >
                    <Icon name="close" />
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      }
    >
      <div>
        <div style={inlineRowStyle}>
          <div style={{ ...fieldValueStyle, flex: '1 1 auto', minWidth: 0 }}>
            {store.name}
          </div>
          {showStatusBadge && (
            <span
              style={{
                ...statusBadgeBaseStyle,
                ...STATUS_BADGE_VARIANT[badgeVariant],
              }}
              data-testid={`settings-store-${store.id}-status`}
            >
              {badgeLabel}
            </span>
          )}
        </div>
        {store.is_online ? (
          <div style={hintStyle}>Online store</div>
        ) : (
          <>
            <div style={hintStyle}>
              {store.address ?? '—'}
              {store.distance_km != null
                ? ` · ${Number(store.distance_km).toFixed(1)} km`
                : ''}
            </div>
            {todayHours && <div style={hintStyle}>Today: {todayHours}</div>}
          </>
        )}
      </div>
    </ListItem>
  );
}

interface StoresCardProps {
  isManager: boolean;
  discoveredFromLocationSave: DiscoveredStore[] | null;
  onConsumeDiscoveredFromLocationSave: () => void;
  /**
   * BUG42 — hide the checklist's internal "Done" dismiss row. That row exists
   * for the Settings surface (the card sits among other cards and the checklist
   * needs a collapse); the first-run wizard adds its OWN primary "Done" that
   * ends the whole flow, so it passes true to keep exactly one Done on screen.
   * Only the dismiss row's render is gated — discovery/persist behavior is
   * untouched. Default false = current Settings behavior.
   */
  hideChecklistDone?: boolean;
  /**
   * BUG57 — online-store candidates carried on a location save's PATCH
   * response (see `LocationCard`'s `onOnlineStores`). Optional / nullable:
   * the Settings surface (`StorePreferencesPage`) has no LocationCard on the
   * same screen and passes null; only the onboarding wizard (which renders
   * both cards in sequence) supplies real values. Regardless of this prop,
   * the online group also populates from this card's own "Find nearby
   * stores" response — see `handleFind`.
   */
  onlineStoresFromLocationSave?: OnlineStoreCandidate[] | null;
}

export function StoresCard({
  isManager,
  discoveredFromLocationSave,
  onConsumeDiscoveredFromLocationSave,
  hideChecklistDone = false,
  onlineStoresFromLocationSave = null,
}: StoresCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const storesQuery = useStores();
  const selectedQuery = useSelectedStores();
  const toggleFavorite = useToggleStoreFavorite();
  const deleteStore = useDeleteStore();
  const household = useCurrentHousehold().data;
  const discover = useDiscoverNearbyStores();
  const addStoresToSelection = useAddStoresToSelection();
  const setSelectedStores = useSetSelectedStores();
  const updateHousehold = useUpdateCurrentHousehold();

  const [error, setError] = useState<string | null>(null);
  // T45 polish — friendly inline guidance shown under the radius input when a
  // commit is rejected (out of range / empty / non-numeric). Distinct from the
  // card-level `error`, which is for save failures.
  const [radiusHint, setRadiusHint] = useState<string | null>(null);
  const [pendingStoreId, setPendingStoreId] = useState<string | null>(null);
  const [discovered, setDiscovered] = useState<DiscoveredStore[] | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  // BUG57 — the online-store lane: its own list + checked map, keyed on
  // `storeLocationId` (online candidates carry no `place_id`/`placeId`, so
  // reusing `discovered`/`checked` above would collide every online row on
  // the same fallback key). Populated from either this card's own "Find
  // nearby stores" response or the `onlineStoresFromLocationSave` prop.
  const [onlineDiscovered, setOnlineDiscovered] = useState<OnlineStoreCandidate[]>([]);
  const [onlineChecked, setOnlineChecked] = useState<Record<string, boolean>>({});
  // Client-side filter over the already-fetched discovered candidates — the
  // discovery list can be long. Filters by name + address; no API call.
  const [checklistFilter, setChecklistFilter] = useState<string>('');
  // T54 — expansion state of the long-checklist collapse (>CHECKLIST_COLLAPSE_LIMIT
  // candidates). Presentation-only; resets on each new discovery run.
  const [checklistExpanded, setChecklistExpanded] = useState<boolean>(false);

  const stores = storesQuery.data?.stores ?? [];
  const selectedIds = new Set(selectedQuery.data?.storeIds ?? []);
  const instant = new Date();

  // T45 — the discovery radius lives here now, next to "Find nearby stores"
  // (which already reads `discovery_radius_km` for its proximity search). It
  // auto-saves on blur/Enter through the shared field model, sending ONLY the
  // radius — coordinates are not required and not clobbered (the household-update
  // patch leaves undefined keys untouched). `useAutoSaveField` owns the
  // controlled-init re-seed, so a typed value no longer snaps back mid-edit
  // (the old render-time resync on the location card is gone).
  //
  // T45 polish — the client mirrors the backend's 1–50 km cap
  // (`updateHouseholdSchema.discoveryRadiusKm` = min(1).max(50)). The hook's
  // `validate` gate stops an out-of-range / empty / non-numeric value from ever
  // firing a PATCH, so the operator never sees a raw 400. `isRadiusValid`
  // exposes that same check to the field's blur/Enter handlers, which restore
  // the last-saved value and surface friendly guidance on a rejected commit.
  const lastSavedRadius = String(household?.discovery_radius_km ?? 10);
  const isRadiusValid = (v: string): boolean => {
    const n = Number(v.trim());
    return v.trim() !== '' && Number.isFinite(n) && n >= RADIUS_MIN_KM && n <= RADIUS_MAX_KM;
  };
  const radius = useAutoSaveField({
    initial: lastSavedRadius,
    validate: isRadiusValid,
    onCommit: async (next) => {
      setError(null);
      try {
        await updateHousehold.mutateAsync({ discoveryRadiusKm: Number(next) });
      } catch (err) {
        setError(apiErrorMessage(err, 'Could not save the discovery radius.'));
      }
    },
  });

  // Commit the radius only when it's in range; otherwise restore the last-saved
  // value and show friendly guidance instead of leaving a blank/invalid number
  // that misrepresents what's persisted. A valid value defers to the hook's own
  // no-op + commit logic (decimals included). Clears the hint on success.
  const commitRadius = () => {
    if (isRadiusValid(radius.value)) {
      setRadiusHint(null);
      radius.onBlur();
      return;
    }
    setRadiusHint(`Radius must be between ${RADIUS_MIN_KM} and ${RADIUS_MAX_KM} km.`);
    radius.setValue(lastSavedRadius);
  };

  // T30.5 — with the "Save checked" button gone, a candidate is selected when
  // its box is checked. On the ONBOARDING bootstrap the pre-checked suggested
  // chains must land in the selection the moment the checklist appears —
  // otherwise their checked state would be a recommendation no click ever
  // commits. Persist the suggested-but-not-yet-selected candidates; each later
  // toggle commits on its own through `handleToggleCandidate`.
  //
  // Callers MUST gate this to the onboarding bootstrap (zero selected stores):
  // the auto-fire path (`discoveredFromLocationSave`, which the backend only
  // populates when the household had zero stores) and the first manual
  // "Find nearby stores". Post-onboarding it must NOT run, or a deliberately
  // deselected suggested chain gets silently re-added.
  const persistSuggested = async (
    candidates: DiscoveredStore[],
    alreadySelectedPlaceIds: Set<string>,
  ) => {
    const toSave = candidates.filter(
      (c) => c.suggestedSelected && !alreadySelectedPlaceIds.has(c.placeId),
    );
    if (toSave.length === 0) return;
    try {
      await addStoresToSelection.mutateAsync({ stores: toSave });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the suggested stores.'));
    }
  };

  useEffect(() => {
    if (discoveredFromLocationSave !== null && discovered === null) {
      setDiscovered(discoveredFromLocationSave);
      const seed: Record<string, boolean> = {};
      for (const c of discoveredFromLocationSave) {
        seed[c.placeId] = c.suggestedSelected;
      }
      setChecked(seed);
      setChecklistExpanded(false);
      onConsumeDiscoveredFromLocationSave();
      // The location save's discovered set carries the suggested chains; persist
      // them so the pre-checked rows reflect a real, saved selection.
      // persistSuggested closes over the latest mutation + setError; it does not
      // need to re-run the effect, so it stays out of the dependency list.
      void persistSuggested(discoveredFromLocationSave, new Set());
    }
  }, [discoveredFromLocationSave, discovered, onConsumeDiscoveredFromLocationSave]);

  // BUG57 — seed the online group from a location save's response. Unlike the
  // physical bootstrap above, there is nothing to persist here: an online
  // candidate has no `suggestedSelected` flag, so it only enters the
  // selection on an explicit check.
  useEffect(() => {
    if (onlineStoresFromLocationSave && onlineStoresFromLocationSave.length > 0) {
      setOnlineDiscovered(onlineStoresFromLocationSave);
      setOnlineChecked((prev) => {
        const next = { ...prev };
        for (const c of onlineStoresFromLocationSave) {
          if (next[c.storeLocationId] === undefined) {
            next[c.storeLocationId] = selectedIds.has(c.storeLocationId);
          }
        }
        return next;
      });
    }
  }, [onlineStoresFromLocationSave]);

  // BUG21 — the favourite ★ flips optimistically in `useToggleStoreFavorite`
  // (with rollback), so this no longer drives a per-row spinner; it only
  // surfaces an error if the server rejects.
  const handleFavorite = async (storeId: string) => {
    setError(null);
    try {
      await toggleFavorite.mutateAsync(storeId);
    } catch (err) {
      setError(apiErrorMessage(err, 'Favorite failed.'));
    }
  };

  // Per-store select toggle — the real household-selection write path. Adding a
  // store appends its id to the selection; deselecting removes it. Goes through
  // the REST replace endpoint (`useSetSelectedStores`), which takes bare store
  // ids — the canonical path for toggling an already-listed store (the discovery
  // checklist below is for adding *newly discovered* candidates).
  const handleToggleSelected = async (storeId: string) => {
    setError(null);
    // BUG21 — `useSetSelectedStores` takes the absolute next id array (a full
    // replace), so the optimistic "read latest cache in the updater" fix can't
    // transfer to it. With the select-checkbox disable gone, two fast toggles on
    // different stores could each read a stale base array and drop one. Compute
    // `nextIds` from the freshest cache (not the render-closure `selectedQuery`)
    // so rapid selects compose.
    const currentIds =
      queryClient.getQueryData<SelectedStoresResponse>(
        keys.households.selectedStores
      )?.storeIds ?? [];
    const nextIds = currentIds.includes(storeId)
      ? currentIds.filter((id) => id !== storeId)
      : [...currentIds, storeId];
    try {
      await setSelectedStores.mutateAsync(nextIds);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update your selection.'));
    }
  };

  const handleDelete = async (store: StoreLocation) => {
    setError(null);
    if (!window.confirm(`Remove "${store.name}"? Linked offers will be unlinked.`)) {
      return;
    }
    setPendingStoreId(store.id);
    try {
      await deleteStore.mutateAsync(store.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Remove failed.'));
    } finally {
      setPendingStoreId(null);
    }
  };

  const handleFind = async () => {
    setError(null);
    setChecklistFilter('');
    setChecklistExpanded(false);
    if (household?.latitude == null || household?.longitude == null) {
      setError('Set your location first.');
      return;
    }
    try {
      const resp = await discover.mutateAsync({
        latitude: Number(household.latitude),
        longitude: Number(household.longitude),
        radiusKm: household.discovery_radius_km
          ? Number(household.discovery_radius_km)
          : 10,
      });
      const candidates = resp.stores as DiscoveredStore[];
      setDiscovered(candidates);
      // BUG57 — the same discover response carries the online lane.
      if (resp.onlineStores && resp.onlineStores.length > 0) {
        setOnlineDiscovered(resp.onlineStores);
        setOnlineChecked((prev) => {
          const next = { ...prev };
          for (const c of resp.onlineStores ?? []) {
            if (next[c.storeLocationId] === undefined) {
              next[c.storeLocationId] = selectedIds.has(c.storeLocationId);
            }
          }
          return next;
        });
      }
      const selectedPlaceIds = new Set(
        stores
          .filter((s) => selectedIds.has(s.id) && s.place_id)
          .map((s) => s.place_id as string)
      );
      // The pre-check + auto-persist of suggested chains is an ONBOARDING
      // bootstrap ONLY — it runs when the household has NO selected stores yet
      // (the first "Find nearby stores"). Post-onboarding (≥1 selected store) a
      // household may have DELIBERATELY deselected a suggested chain (e.g.
      // removed Walmart); re-checking + re-adding it here would silently
      // resurrect it and override their choice. So once there's a selection,
      // seed the checkboxes from the CURRENT selection ONLY (a deselected chain
      // shows unchecked) and write nothing — only an explicit user check adds a
      // store.
      const isOnboardingBootstrap = selectedIds.size === 0;
      const seed: Record<string, boolean> = {};
      for (const c of candidates) {
        const preChecked = isOnboardingBootstrap
          ? c.suggestedSelected || selectedPlaceIds.has(c.placeId)
          : selectedPlaceIds.has(c.placeId);
        seed[c.placeId] = preChecked;
      }
      setChecked(seed);
      if (isOnboardingBootstrap) {
        // First run: persist the suggested chains so the pre-checked rows reflect
        // a real, saved selection. Subsequent toggles commit individually.
        await persistSuggested(candidates, selectedPlaceIds);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Discovery failed.'));
    }
  };

  // T30.5 — the post-discovery selection auto-saves on toggle (no "Save
  // checked" button). Checking a candidate adds it to the selection immediately
  // (find-or-create + append via `addStoresToSelection`; re-saving an
  // already-selected store changes nothing); unchecking removes the now-selected
  // store via the selection-replace path once it has materialized in the store
  // list. The "Find nearby stores" discovery TRIGGER stays an explicit button —
  // it's an action, not a value.
  const handleToggleCandidate = async (placeId: string) => {
    const turningOn = !checked[placeId];
    // Optimistic checkbox flip so the control responds instantly.
    setChecked((prev) => ({ ...prev, [placeId]: turningOn }));
    setError(null);
    try {
      if (turningOn) {
        const candidate = (discovered ?? []).find((c) => c.placeId === placeId);
        if (candidate) {
          await addStoresToSelection.mutateAsync({ stores: [candidate] });
        }
      } else {
        // Unchecking: drop it from the selection if it has a materialized store
        // row (it does once the add has saved). No matching row → nothing to
        // remove (it was never persisted), so the local uncheck is enough.
        const match = stores.find(
          (s) => s.place_id === placeId && selectedIds.has(s.id)
        );
        if (match) {
          const nextIds = (selectedQuery.data?.storeIds ?? []).filter(
            (id) => id !== match.id
          );
          await setSelectedStores.mutateAsync(nextIds);
        }
      }
    } catch (err) {
      // Roll the checkbox back to its pre-toggle state on a failed write.
      setChecked((prev) => ({ ...prev, [placeId]: !turningOn }));
      setError(apiErrorMessage(err, 'Could not update your selection.'));
    }
  };

  // BUG57 — the online-lane analogue of `handleToggleCandidate`, keyed on
  // `storeLocationId` (never `placeId` — online candidates carry no
  // `placeId`, so this is what keeps two online rows from colliding on one
  // key). Goes through the SAME `/import-stores` mutation the physical rows
  // use: `resolveStoreLocationId` on the backend already prefers
  // `storeLocationId` over `placeId`, so no new selection path is needed.
  const handleToggleOnlineCandidate = async (storeLocationId: string) => {
    const turningOn = !onlineChecked[storeLocationId];
    setOnlineChecked((prev) => ({ ...prev, [storeLocationId]: turningOn }));
    setError(null);
    try {
      if (turningOn) {
        const candidate = onlineDiscovered.find(
          (c) => c.storeLocationId === storeLocationId
        );
        if (candidate) {
          await addStoresToSelection.mutateAsync({ stores: [candidate] });
        }
      } else {
        // Unchecking: an online candidate's materialized store row has
        // `id === storeLocationId` (the catalog row IS the resolved id), so
        // match on `id`, not `place_id` (online rows carry none).
        const match = stores.find(
          (s) => s.id === storeLocationId && selectedIds.has(s.id)
        );
        if (match) {
          const nextIds = (selectedQuery.data?.storeIds ?? []).filter(
            (id) => id !== match.id
          );
          await setSelectedStores.mutateAsync(nextIds);
        }
      }
    } catch (err) {
      setOnlineChecked((prev) => ({ ...prev, [storeLocationId]: !turningOn }));
      setError(apiErrorMessage(err, 'Could not update your selection.'));
    }
  };

  const handleDismissChecklist = () => {
    setDiscovered(null);
    setChecked({});
    setChecklistFilter('');
    setChecklistExpanded(false);
  };

  // Client-side filter of the discovered candidates by name + address (case-
  // insensitive). Filters the already-fetched `discovered` array — no API call.
  //
  // Two passes, gated the same way the deals search gates its rewrite: repair
  // only runs once the typed query has returned nothing. Pass 1 is the plain
  // substring match — queries that already worked behave identically. Pass 2
  // splits the query into words, runs each through the shared trigram repair
  // against a vocabulary built from the candidates themselves, and requires
  // every repaired word to appear in the name or address. That is what finds
  // "Kay's" when the shopper types "Kays" — repair now ignores punctuation, so
  // the missing apostrophe scores as an exact hit.
  const filteredDiscovered = (() => {
    const q = checklistFilter.trim().toLowerCase();
    const all = discovered ?? [];
    if (q === '') return all;

    const fields = (c: DiscoveredStore) => [
      c.name.toLowerCase(),
      (c.address ?? '').toLowerCase(),
    ];
    const hasWord = (c: DiscoveredStore, word: string) =>
      fields(c).some((field) => field.includes(word));

    const exact = all.filter((c) => hasWord(c, q));
    if (exact.length > 0) return exact;

    const catalog: RewriteCatalog = {
      synonyms: [],
      candidateWords: [
        ...new Set(all.flatMap((c) => fields(c).flatMap((field) => field.split(/\s+/)).filter(Boolean))),
      ],
    };
    const repaired = q
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => repairTypoTerm(word, catalog).term);
    if (repaired.length === 0) return exact;

    return all.filter((c) => repaired.every((word) => hasWord(c, word)));
  })();

  // T54 — render-time collapse of a long checklist. With no filter text and
  // more than CHECKLIST_COLLAPSE_LIMIT candidates, show only the nearest
  // CHECKLIST_COLLAPSE_LIMIT rows PLUS every checked row (a checked row is
  // never hidden while collapsed), preserving distance order. A non-empty
  // filter bypasses the collapse entirely: it searches and shows ALL matches,
  // and clearing it restores the prior collapsed/expanded state (the slice
  // derives from `checklistExpanded`, which filtering never touches).
  const checklistCollapsible =
    checklistFilter.trim() === '' &&
    filteredDiscovered.length > CHECKLIST_COLLAPSE_LIMIT;
  const visibleDiscovered =
    checklistCollapsible && !checklistExpanded
      ? filteredDiscovered.filter(
          (c, index) =>
            index < CHECKLIST_COLLAPSE_LIMIT || Boolean(checked[c.placeId]),
        )
      : filteredDiscovered;

  return (
    <Card
      padding="none"
      shadow="none"
      style={setgroupCardStyle}
      data-testid="settings-stores"
    >
      <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
        <h3 style={sectionHeaderStyle}>Your stores</h3>
        <p style={hintStyle}>Offers only show for the stores in this list.</p>
      </div>
      {error && (
        <div style={{ ...emptyStyle, ...errorStyle }} role="alert">
          {error}
        </div>
      )}
      {storesQuery.isLoading ? (
        <div style={emptyStyle}>
          <Spinner />
        </div>
      ) : stores.length === 0 ? (
        <div style={emptyStyle} data-testid="settings-stores-empty">
          No stores yet. Use "Find nearby stores" below to add some.
        </div>
      ) : (
        <List data-testid="settings-stores-list">
          {stores.map((store, index) => (
            <StoreRow
              key={store.id}
              store={store}
              divided={index > 0}
              checked={selectedIds.has(store.id)}
              isManager={isManager}
              pendingDelete={pendingStoreId === store.id && deleteStore.isPending}
              instant={instant}
              onToggleSelected={handleToggleSelected}
              onToggleFavorite={handleFavorite}
              onDelete={handleDelete}
            />
          ))}
        </List>
      )}

      {isManager && (
        <div
          style={{
            padding: 'var(--space-3) var(--space-6) var(--space-4)',
            borderTopWidth: '0.0625rem',
            borderTopStyle: 'solid',
            borderTopColor: 'var(--color-border-muted)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          {household?.store_discovery_available && (
            <Input
              label="Discovery radius (km)"
              type="number"
              min="1"
              max="50"
              step="1"
              value={radius.value}
              onChange={(e) => {
                if (radiusHint) setRadiusHint(null);
                radius.setValue(e.target.value);
              }}
              onBlur={commitRadius}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRadius();
                }
              }}
              data-testid="settings-stores-radius"
              {...(radiusHint ? { error: radiusHint } : {})}
              hint="How far to look for nearby stores (1–50 km). Changes save automatically."
            />
          )}
          {/* TD39 — the "Add online store" button (which opened a store-CREATE
              editor) is gone: `storelocations` is platform-authored. Adding an
              online store now means selecting an existing curated online-store row
              through the discovery/selection flow; adding an UNLISTED online store
              waits on the store-request feature (known gap, ADR-004 D3). */}
          <div style={inlineRowStyle}>
            {household?.store_discovery_available && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFind}
                loading={discover.isPending}
                disabled={discover.isPending}
                data-testid="settings-stores-find"
              >
                Find nearby stores
              </Button>
            )}
          </div>
          {discovered && discovered.length === 0 && (
            <div
              style={{ ...hintStyle, marginTop: 'var(--space-2)' }}
              data-testid="settings-stores-discovered-empty"
            >
              No nearby stores found.
            </div>
          )}
          {discovered && discovered.length > 0 && (
            <div data-testid="settings-stores-checklist">
              <p
                style={{ ...hintStyle, marginTop: 'var(--space-3)' }}
                data-testid="settings-stores-checklist-explainer"
              >
                We found grocery stores within{' '}
                {household?.discovery_radius_km
                  ? Number(household.discovery_radius_km)
                  : 10}{' '}
                km of your address. The major chains are pre-selected — uncheck
                any you don't shop at, check others if you do. Each change saves
                automatically.
              </p>
              {/* Client-side search over the fetched candidates — the list can be
                  long. Filters by name + address; no API call. */}
              <Input
                label=""
                type="search"
                value={checklistFilter}
                onChange={(e) => setChecklistFilter(e.target.value)}
                placeholder="Filter by name or address"
                aria-label="Filter discovered stores"
                data-testid="settings-stores-checklist-search"
              />
              {filteredDiscovered.length === 0 ? (
                <div
                  style={{ ...hintStyle, marginTop: 'var(--space-2)' }}
                  data-testid="settings-stores-checklist-no-matches"
                >
                  No stores match "{checklistFilter.trim()}".
                </div>
              ) : (
                <List>
                  {visibleDiscovered.map((store, index) => (
                    <ListItem
                      key={store.placeId}
                      divided={index > 0}
                      data-testid={`settings-discovered-${store.placeId}`}
                      leading={
                        <input
                          type="checkbox"
                          checked={Boolean(checked[store.placeId])}
                          disabled={addStoresToSelection.isPending}
                          onChange={() => void handleToggleCandidate(store.placeId)}
                          aria-label={`Select ${store.name}`}
                          data-testid={`settings-discovered-${store.placeId}-check`}
                        />
                      }
                    >
                      <div>
                        <div style={fieldValueStyle}>{store.name}</div>
                        <div style={hintStyle}>
                          {store.address} · {Number(store.distanceKm).toFixed(1)} km
                        </div>
                      </div>
                    </ListItem>
                  ))}
                </List>
              )}
              {/* T54 — the collapse toggle sits under the visible rows and
                  ABOVE the dismiss row, keeping the Settings "Done" the
                  block's last row. Hidden while filtering (the filter shows
                  all matches) and when the list fits within the head. */}
              {checklistCollapsible && (
                <div style={{ ...inlineRowStyle, marginTop: 'var(--space-2)' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChecklistExpanded((prev) => !prev)}
                    data-testid="settings-stores-checklist-showall"
                  >
                    {checklistExpanded
                      ? 'Show fewer stores'
                      : `Show all ${filteredDiscovered.length} stores`}
                  </Button>
                </div>
              )}
              {!hideChecklistDone && (
                <div
                  style={{
                    ...inlineRowStyle,
                    justifyContent: 'flex-end',
                    marginTop: 'var(--space-2)',
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDismissChecklist}
                    data-testid="settings-stores-checklist-done"
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          )}
          {/* BUG57 — the online-store lane: its own visually separate group,
              rendered regardless of `store_discovery_available` (decision:
              that flag guards the Places-backed physical geo-search; the
              online lane is postal-code region eligibility the backend
              computes unconditionally on every PATCH / discover-stores call,
              with no dependency on Places availability). No distance or
              address — an online candidate carries neither. Keyed on
              `storeLocationId` only (never `placeId`, which online rows
              don't carry) so two online candidates never collide on one
              React key / checkbox-state key / testid. */}
          {onlineDiscovered.length > 0 && (
            <div
              data-testid="settings-stores-online"
              style={{ marginTop: 'var(--space-3)' }}
            >
              <p
                style={hintStyle}
                data-testid="settings-stores-online-explainer"
              >
                These retailers sell online to your area — add them even
                without a physical store nearby. Each change saves
                automatically.
              </p>
              <List>
                {onlineDiscovered.map((store, index) => (
                  <ListItem
                    key={store.storeLocationId}
                    divided={index > 0}
                    data-testid={`settings-online-${store.storeLocationId}`}
                    leading={
                      <input
                        type="checkbox"
                        checked={Boolean(onlineChecked[store.storeLocationId])}
                        disabled={addStoresToSelection.isPending}
                        onChange={() =>
                          void handleToggleOnlineCandidate(store.storeLocationId)
                        }
                        aria-label={`Select ${store.name}`}
                        data-testid={`settings-online-${store.storeLocationId}-check`}
                      />
                    }
                  >
                    <div style={fieldValueStyle}>{store.name}</div>
                  </ListItem>
                ))}
              </List>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function applySessionMutation(
  resp: HouseholdMembershipMutationResponse,
  tokens: ReturnType<typeof useTokens>
): void {
  if (resp.sessionToken && resp.user) {
    tokens.setSession({ accessToken: resp.sessionToken, user: resp.user });
  }
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
